import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import {
    Box, Tabs, Tab, Stack, Typography, Button, List, ListItemButton, ListItemText,
    Chip, CircularProgress, Alert, TextField, FormControl, InputLabel, Select, MenuItem,
    Paper, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import TicketConversation from '../Componant/Tickets/TicketConversation';
import { useTicketSocket } from '../hooks/useTicketSocket';
import { UserContext } from '../Context/UserContext';
import { TranslationContext } from '../Context/TranslationContext';
import { useTranslation } from '../hooks/useTranslation';
import { LOCALE_BY_LANG, formatFileSize } from '../Utils/i18nHelpers';
import {
    listTickets, createTicket, uploadTicketAttachments, addTicketMessage, SERVICES, PRIORITY_SERVICES
} from '../Utils/ticketApi';

const STATUS_COLORS = {
    open: 'primary',
    in_progress: 'info',
    resolved: 'success',
    closed: 'default',
};

const PRIORITY_VALUES = ['low', 'medium', 'high', 'urgent'];
const REQUEST_TYPE_VALUES = ['bug', 'question', 'feature', 'billing'];

const OTHER_APPLICATION = '__other__';

const initialForm = { title: '', description: '', priority: '', application: '', applicationOther: '', requestType: '', pageUrl: '' };

export default function TicketsPage() {
    const { userInfo } = useContext(UserContext);
    const { currentLanguage } = useContext(TranslationContext);
    const t = useTranslation('Tickets.json');
    const tCommon = useTranslation('Common.json');
    const applications = userInfo?.applications || [];

    const TABS = [
        { label: t('tickets.tab.support'), service: SERVICES.SUPPORT },
        { label: t('tickets.tab.commercial'), service: SERVICES.COMMERCIAL },
    ];
    const STATUS_LABELS = {
        open: t('tickets.status.open'),
        in_progress: t('tickets.status.in_progress'),
        resolved: t('tickets.status.resolved'),
        closed: t('tickets.status.closed'),
    };
    const PRIORITIES = PRIORITY_VALUES.map(value => ({ value, label: t(`tickets.priority.${value}`) }));
    const REQUEST_TYPES = REQUEST_TYPE_VALUES.map(value => ({ value, label: t(`tickets.request_type.${value}`) }));
    const REQUEST_TYPE_LABELS = REQUEST_TYPE_VALUES.reduce((acc, value) => ({ ...acc, [value]: t(`tickets.request_type.${value}`) }), {});
    const formatSize = (bytes) => formatFileSize(bytes, t);

    const [tab, setTab] = useState(0);
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTicketId, setSelectedTicketId] = useState(null);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [stagedFiles, setStagedFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    const currentService = TABS[tab].service;
    const showPriority = PRIORITY_SERVICES.includes(currentService);

    // Permet à d'autres pages (ex: DemoPage "Contacter un commercial") de
    // naviguer ici avec un onglet et le dialogue de création déjà ouverts.
    const location = useLocation();
    useEffect(() => {
        if (!location.state?.openCreate) return;
        const tabIndex = TABS.findIndex((t) => t.service === location.state.service);
        if (tabIndex >= 0) setTab(tabIndex);
        setCreating(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const loadTickets = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await listTickets({ service: currentService, limit: 50 });
            setTickets(result.data || []);
        } catch (e) {
            setError(tCommon('common.error.generic'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setSelectedTicketId(null);
        setCreating(false);
        loadTickets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    useTicketSocket({
        onTicketCreated: (newTicket) => {
            if (newTicket.service === currentService) {
                setTickets(prev => (prev.some(x => x.id === newTicket.id) ? prev : [newTicket, ...prev]));
            }
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const closeCreateDialog = () => {
        setCreating(false);
        setForm(initialForm);
        setStagedFiles([]);
    };

    const handleFilesSelected = (e) => {
        const files = Array.from(e.target.files || []);
        setStagedFiles(prev => [...prev, ...files]);
        e.target.value = '';
    };

    const removeStagedFile = (index) => {
        setStagedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            const payload = { title: form.title, description: form.description, service: currentService };
            if (showPriority && form.priority) payload.priority = form.priority;
            const applicationValue = form.application === OTHER_APPLICATION ? form.applicationOther.trim() : form.application;
            if (applicationValue) payload.application = applicationValue;
            if (showPriority && form.requestType) payload.requestType = form.requestType;
            if (showPriority && form.pageUrl) payload.pageUrl = form.pageUrl;
            const ticket = await createTicket(payload);

            if (stagedFiles.length > 0) {
                const attachments = await uploadTicketAttachments(ticket.id, stagedFiles);
                await addTicketMessage(ticket.id, t('tickets.initial_attachments_message'), attachments);
            }

            closeCreateDialog();
            await loadTickets();
            setSelectedTicketId(ticket.id);
        } catch (e2) {
            setError(e2.response?.data?.error_description || tCommon('common.error.generic'));
        } finally {
            setSubmitting(false);
        }
    };

    if (selectedTicketId) {
        return (
            <Box sx={{ width: '100%', p: 3, display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 30px)' }}>
                <Paper elevation={1} sx={{ borderRadius: 3, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <TicketConversation
                        ticketId={selectedTicketId}
                        onBack={() => { setSelectedTicketId(null); loadTickets(); }}
                    />
                </Paper>
            </Box>
        );
    }

    const locale = LOCALE_BY_LANG[currentLanguage] || LOCALE_BY_LANG.fr;

    return (
        <Box sx={{ width: '100%', p: 3, display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 30px)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>{t('tickets.title')}</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreating(true)}
                    sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                >
                    {t('tickets.new_ticket')}
                </Button>
            </Stack>

            <Tabs value={tab} onChange={(_e, v) => setTab(v)}
                  TabIndicatorProps={{ style: { background: '#FF7233' } }}
                  sx={{ '& .Mui-selected': { color: '#FF7233 !important' }, mb: 2 }}>
                {TABS.map(tabItem => <Tab key={tabItem.service} label={tabItem.label} />)}
            </Tabs>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Dialog open={creating} onClose={closeCreateDialog} fullWidth maxWidth="sm">
                <DialogTitle>{t('tickets.dialog.title', [TABS[tab].label])}</DialogTitle>
                <form onSubmit={handleCreate}>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField
                            name="title" label={t('tickets.field.title')} value={form.title} onChange={handleChange}
                            required fullWidth inputProps={{ maxLength: 255 }} autoFocus
                        />
                        <TextField
                            name="description" label={t('tickets.field.description')} value={form.description} onChange={handleChange}
                            required fullWidth multiline rows={4}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="application-label">{t('tickets.field.application')}</InputLabel>
                            <Select
                                labelId="application-label" name="application" label={t('tickets.field.application')}
                                value={form.application} onChange={handleChange}
                            >
                                <MenuItem value="">{t('tickets.field.application.none')}</MenuItem>
                                {applications.map(app => (
                                    <MenuItem key={app.name} value={app.name}>{app.name}</MenuItem>
                                ))}
                                <MenuItem value={OTHER_APPLICATION}>{t('tickets.field.application.other')}</MenuItem>
                            </Select>
                        </FormControl>
                        {form.application === OTHER_APPLICATION && (
                            <TextField
                                name="applicationOther" label={t('tickets.field.application_other')}
                                value={form.applicationOther} onChange={handleChange}
                                fullWidth autoFocus
                            />
                        )}
                        {showPriority && (
                            <FormControl fullWidth>
                                <InputLabel id="priority-label">{t('tickets.field.priority')}</InputLabel>
                                <Select
                                    labelId="priority-label" name="priority" label={t('tickets.field.priority')}
                                    value={form.priority} onChange={handleChange}
                                >
                                    {PRIORITIES.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                        )}
                        {showPriority && (
                            <FormControl fullWidth>
                                <InputLabel id="request-type-label">{t('tickets.field.request_type')}</InputLabel>
                                <Select
                                    labelId="request-type-label" name="requestType" label={t('tickets.field.request_type')}
                                    value={form.requestType} onChange={handleChange}
                                >
                                    <MenuItem value="">{t('tickets.field.request_type.none')}</MenuItem>
                                    {REQUEST_TYPES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                        )}
                        {showPriority && (
                            <TextField
                                name="pageUrl" label={t('tickets.field.page_url', [tCommon('common.optional')])}
                                value={form.pageUrl} onChange={handleChange}
                                fullWidth placeholder="https://..."
                            />
                        )}

                        <Box>
                            <input ref={fileInputRef} type="file" multiple hidden onChange={handleFilesSelected} />
                            <Button
                                startIcon={<AttachFileIcon />}
                                onClick={() => fileInputRef.current?.click()}
                                sx={{ color: 'primary.main' }}
                            >
                                {t('tickets.attach_file')}
                            </Button>
                            {stagedFiles.length > 0 && (
                                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                                    {stagedFiles.map((file, i) => (
                                        <Chip
                                            key={i}
                                            label={`${file.name} · ${formatSize(file.size)}`}
                                            onDelete={() => removeStagedFile(i)}
                                            deleteIcon={<CloseIcon />}
                                            size="small"
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={closeCreateDialog} disabled={submitting}>
                            {tCommon('common.cancel')}
                        </Button>
                        <Button
                            type="submit" variant="contained"
                            disabled={submitting || !form.title || !form.description}
                            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : t('tickets.create')}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Paper elevation={1} sx={{ borderRadius: 3, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                        <CircularProgress />
                    </Box>
                ) : tickets.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, textAlign: 'center' }}>
                        <Typography color="text.secondary">{t('tickets.empty', [TABS[tab].label.toLowerCase()])}</Typography>
                    </Box>
                ) : (
                    <List disablePadding>
                        {tickets.map(ticket => (
                            <ListItemButton key={ticket.id} onClick={() => setSelectedTicketId(ticket.id)} sx={{ py: 1.5 }}>
                                <ListItemText
                                    primary={ticket.title}
                                    secondary={[ticket.application, REQUEST_TYPE_LABELS[ticket.request_type], new Date(ticket.updated_at || ticket.created_at).toLocaleString(locale)].filter(Boolean).join(' · ')}
                                />
                                <Stack direction="row" spacing={1}>
                                    {ticket.priority && <Chip size="small" variant="outlined" label={t(`tickets.priority.${ticket.priority}`)} />}
                                    <Chip size="small" label={STATUS_LABELS[ticket.status] || ticket.status} color={STATUS_COLORS[ticket.status] || 'default'} />
                                </Stack>
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </Paper>
        </Box>
    );
}
